/**
 * JourneySummary Widget
 *
 * Displays a summary of the configured journey: route, purposes, family, features.
 * Uses COUNTRIES from @tonnie/foundation for country data.
 */

import { useTranslation, useCountryName } from '@tonnie/locale';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CountryFlag,
} from '@tonnie/ui';
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  MapPin,
  Sparkles,
  Users,
} from '@tonnie/assets';
import { Link } from 'wouter';
import type { WidgetProps } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface IJourneySummaryConfig {
  /** Country codes may come from different sources with different keys */
  originCountry?: string;
  destinationCountry?: string;
  /** From country selector widget result */
  origin?: string;
  destination?: string;
  purpose?: string;
  purposes?: string[];
  bringFamily?: boolean;
  familyProgram?: boolean;
  scope?: Record<string, boolean>;
  /** Engine may also pass the checklist widget result as an array */
  [key: string]: unknown;
}

/**
 * JourneySummaryWidget - Displays a comprehensive summary of the configured journey.
 *
 * Renders the journey route (origin/destination countries), selected purposes,
 * family configuration, active features, and a call-to-action to unlock the journey.
 * Uses country data from `@tonnie/foundation` for flag and name resolution.
 *
 * @param config - Journey summary data (originCountry, destinationCountry, purposes, bringFamily, familyProgram, scope)
 * @param onComplete - Callback fired when the user clicks "Create My Journey"
 * @returns React component for journey summary display
 */
export function JourneySummaryWidget({
  config,
  onComplete,
}: WidgetProps & { config?: IJourneySummaryConfig }) {
  const { t } = useTranslation();
  const countryName = useCountryName();

  // Resolve country codes — check multiple possible field names
  // (originCountry from initial variables, origin from country selector widget)
  const originCode = (config?.originCountry ?? config?.origin ?? '') as string;
  const destCode = (config?.destinationCountry ?? config?.destination ?? '') as string;

  // Resolve purposes — the checklist widget stores its result in a feature_state
  // which the engine captures. The config may have it as purposes array or single purpose.
  const purposes = config?.purposes
    ? (config.purposes as string[])
    : config?.purpose
      ? [config.purpose as string]
      : [];
  const bringFamily = config?.bringFamily === true || String(config?.bringFamily) === 'true';
  const familyProgram = config?.familyProgram === true || String(config?.familyProgram) === 'true';
  const scope = (config?.scope ?? {}) as Record<string, boolean>;
  const activeFeatures = Object.entries(scope)
    .filter(([, active]) => active)
    .map(([key]) => key);

  /** Purpose label lookup using i18n with fallbacks */
  const PURPOSE_LABELS: Record<string, string> = {
    checkup: t('journeyBuilder.purposeCheckup'),
    job: t('journeyBuilder.purposeJob'),
    relocation: t('journeyBuilder.purposeRelocation'),
    culture: t('journeyBuilder.purposeCulture'),
    care: t('journeyBuilder.purposeCare'),
  };

  return (
    <div className="w-full space-y-4 py-2" data-testid="widget-journey_summary">
      <Card className="border-2 border-primary/20 bg-linear-to-br from-primary/5 to-background">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-primary-accessible" />
            {t('copilot.journeySummary')}
          </CardTitle>
          <CardDescription>{t('copilot.reviewJourney')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Route */}
          <div className="space-y-1.5" data-testid="widget-journey_summary-field-route">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <MapPin className="w-4 h-4" />
              {t('copilot.journeyRoute')}
            </div>
            <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 flex-1">
                <CountryFlag code={originCode} size="xl" />
                <div>
                  <p className="font-semibold text-sm">
                    {originCode ? countryName(originCode) : t('common.origin')}
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-primary-accessible shrink-0" />
              <div className="flex items-center gap-2 flex-1">
                <CountryFlag code={destCode} size="xl" />
                <div>
                  <p className="font-semibold text-sm">
                    {destCode ? countryName(destCode) : t('common.destination')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Purposes */}
          {purposes.length > 0 && (
            <div className="space-y-1.5" data-testid="widget-journey_summary-field-purposes">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Sparkles className="w-4 h-4" />
                {purposes.length > 1 ? t('copilot.journeyPurposes') : t('copilot.journeyPurpose')}
              </div>
              <div className="p-3 bg-background rounded-lg border border-border space-y-2">
                {purposes.map((p) => (
                  <div key={p} className="flex items-start gap-2 p-2 rounded-md bg-muted/30">
                    <CheckCircle2 className="w-4 h-4 text-primary-accessible shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{PURPOSE_LABELS[p] ?? p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Family */}
          <div className="space-y-1.5" data-testid="widget-journey_summary-field-family">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Users className="w-4 h-4" />
              {t('copilot.familyConfig')}
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="font-medium text-sm">
                {bringFamily
                  ? familyProgram
                    ? t('copilot.familyProgramEnabled')
                    : t('copilot.bringingFamilyNoProgram')
                  : t('copilot.individualJourney')}
              </p>
              {familyProgram && (
                <Badge variant="secondary" className="mt-2">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('copilot.familyAddon')}
                </Badge>
              )}
            </div>
          </div>

          {/* Features */}
          {activeFeatures.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <CheckCircle2 className="w-4 h-4" />
                {t('copilot.includedFeatures')}
              </div>
              <div className="p-3 bg-background rounded-lg border border-border flex flex-wrap gap-2">
                {activeFeatures.map((f) => (
                  <Badge key={f} variant="outline" className="capitalize">
                    {f}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Unlock info */}
          <div className="p-4 bg-linear-to-br from-primary/15 to-primary/5 border-2 border-primary/30 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                <CreditCard className="w-5 h-5 text-primary-accessible" />
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold">{t('copilot.journeyReady')}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t('copilot.unlockNote')}{' '}
                  <Link
                    href="/billing"
                    className="text-primary-accessible hover:underline font-medium inline-flex items-center gap-0.5"
                  >
                    {t('copilot.billingPage')} <ExternalLink className="w-3 h-3" />
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          {onComplete && (
            <div className="flex gap-3 pt-2">
              <Button
                variant="default"
                className="flex-1"
                onClick={() => onComplete?.()}
                data-testid="widget-journey_summary-confirm"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {t('copilot.createMyJourney')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
